import React, { useMemo, useCallback, useState } from "react";
import "react-lazy-load-image-component/src/effects/blur.css";
import { VariableSizeList as List } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import ReactTooltip from "react-tooltip";
import { RowList } from "./RowList";
import memoizeOne from "memoize-one";
import { sanitizeHtml } from "../../common/components/SafeHtml";

const createItemData = memoizeOne(
    (
        columnCount,
        imageData,
        handleMultiSelectImage,
        selectedImages,
        handleEdit,
        handleFavClick,
        isAssignView,
        hasEdit,
        viewRecommendations,
        viewImageModal,
        showSelectBox,
        handleInputCaptionData,
        viewAssets,
        touchedImageId,
        isSmartChartView,
        isNextPageLoading,
        entityPluralLabel,
        entityLabel,
        imageBadges
    ) => ({
        columnCount,
        imageData,
        handleMultiSelectImage,
        selectedImages,
        handleEdit,
        handleFavClick,
        isAssignView,
        hasEdit,
        viewRecommendations,
        viewImageModal,
        showSelectBox,
        handleInputCaptionData,
        viewAssets,
        touchedImageId,
        isSmartChartView,
        isNextPageLoading,
        entityPluralLabel,
        entityLabel,
        imageBadges
    })
);

function ImageGrid({
    imageData,
    showSelectBox,
    selectedImages,
    handleMultiSelectImage,
    viewImageModal,
    handleEdit,
    handleDelete,
    hasMore,
    fetchMoreImages,
    isAssignView,
    hasEdit,
    hasDelete,
    handleFavClick,
    viewRecommendations,
    height,
    width,
    handleInputCaptionData,
    viewAssets,
    listRef,
    touchedImageId,
    isSmartChartView,
    isNextPageLoading,
    entityPluralLabel = "Recommendations",
    entityLabel = "Recommendation",
    // Optional `{ imageId: { icon, tip } }`. A page that has something extra to say about particular images
    // supplies it (the Image Analyse page marks the ones Otto has already read); everywhere else it is
    // undefined and nothing is drawn.
    imageBadges
}) {
    const [initialLoad, setInitialLoad] = useState(true);
    const cardHeight = 266;
    const columnCount = isAssignView ? 6 : 5;
    const rowCount = Math.ceil(imageData.length / columnCount);

    const getRowHeight = useMemo(() => {
        return () => {
            return cardHeight + 6;
        };
    }, [cardHeight]);

    const itemData = createItemData(
        columnCount,
        imageData,
        handleMultiSelectImage,
        selectedImages,
        handleEdit,
        handleFavClick,
        isAssignView,
        hasEdit,
        viewRecommendations,
        viewImageModal,
        showSelectBox,
        handleInputCaptionData,
        viewAssets,
        touchedImageId,
        isSmartChartView,
        isNextPageLoading,
        entityPluralLabel,
        entityLabel,
        imageBadges
    );

    const itemCount = hasMore ? rowCount + 1 : rowCount;

    const loadMoreItems = useCallback(() => {
        if (isNextPageLoading || initialLoad) {
            setInitialLoad(false);
            return;
        }
        fetchMoreImages();
    }, [fetchMoreImages, isNextPageLoading, initialLoad]);

    const isItemLoaded = index => !hasMore || index < rowCount;

    const handleItemsRendered = ({ visibleStopIndex }) => {
        const threshold = itemCount - 1;
        if (visibleStopIndex >= threshold) {
            loadMoreItems();
        }
    };

    return (
        <InfiniteLoader isItemLoaded={isItemLoaded} itemCount={itemCount} loadMoreItems={loadMoreItems} threshold={1}>
            {({ onItemsRendered, ref }) => (
                <>
                    <ReactTooltip
                        id="image-grid"
                        effect="solid"
                        place="top"
                        backgroundColor="#1383D9"
                        getContent={dataTip => (dataTip ? sanitizeHtml(dataTip) : "")}
                        html={true}
                        multiline={true}
                    />
                    <List
                        className="list"
                        width={width}
                        height={height}
                        itemData={itemData}
                        ref={listRef}
                        itemCount={itemCount}
                        itemSize={getRowHeight}
                        estimatedItemSize={cardHeight}
                        onItemsRendered={({ visibleStartIndex, visibleStopIndex }) => {
                            handleItemsRendered({ visibleStopIndex });
                            onItemsRendered({ visibleStartIndex, visibleStopIndex });
                        }}
                    >
                        {RowList}
                    </List>
                </>
            )}
        </InfiniteLoader>
    );
}

export default React.memo(ImageGrid);
